import { defineStore } from 'pinia';

import type { SidePanel } from '@/components/side-panels/types';

import * as SidePanels from '@/components/side-panels';

type Nullable<T> = T | null | undefined;

export type SidePanelId = number;
export type SidePanelName = keyof typeof SidePanels;
export type SpawnProps = Record<string, any>;
export type SpawnEvents = Record<string, Nullable<CallableFunction>>;

export interface SpawnObjectPayload { name: SidePanelName, props?: SpawnProps, events?: SpawnEvents, closeVisible?: boolean }
export type SpawnPayload = SidePanelName | SpawnObjectPayload;

export type ActiveSidePanel = SidePanel.VueComponent & Required<SpawnObjectPayload> & { id: SidePanelId };
interface StoreState {
  activeSidePanels: Ref<ActiveSidePanel[]>
  nextPanelId: SidePanelId
}

type ComparatorActiveSidePanel<T> = (value: T) => (sidePanel: ActiveSidePanel) => boolean;
const findById: ComparatorActiveSidePanel<SidePanelId> = (value) => (sidePanel) => sidePanel.id === value;
const findByName: ComparatorActiveSidePanel<SidePanelName> = (value) => (sidePanel) => sidePanel.name === value;

export const useSidepanelStore = defineStore('side-panel', {
  state: (): StoreState => ({
    activeSidePanels: ref([]),

    nextPanelId: 1,
  }),

  getters: {
    panelsNamesInUse(state) {
      return state.activeSidePanels.reduce((result, value) => {
        if (result.includes(value.name))
          return result;

        result.push(value.name);
        return result;
      }, [] as SidePanelName[]);
    },

    visibleSidePanel(state) {
      const length = state.activeSidePanels.length;
      if (!length)
        return null;

      return state.activeSidePanels[length - 1];
    },
  },

  actions: {
    spawn(payload: SpawnPayload) {
      let name: SidePanelName, props: Nullable<SpawnProps>, events: Nullable<SpawnEvents>, closeVisible: Nullable<boolean>;

      if (typeof payload === 'object') {
        if (Array.isArray(payload)) {
          /** @deprecated Массив как параметр теперь не будет поддерживаться */
          [name, props, events, closeVisible = true] = payload;
        }
        else {
          ({ name, props, events, closeVisible = true } = payload);
        }
      }
      else if (typeof payload === 'string') {
        name = payload as SidePanelName;
      }
      else {
        throw new TypeError('Unknown payload structure');
      }

      const sidePanel = name && SidePanels[name];
      if (!sidePanel)
        return; // TODO: throw error?

      const id = this.nextPanelId++;
      const activeSidePanel: ActiveSidePanel = Object.assign({ id, name, props: props || {}, events: events || {}, closeVisible: closeVisible || true }, sidePanel);

      this.activeSidePanels.push(activeSidePanel);

      return id;
    },

    despawn(...args: unknown[]) {
      const firstArgumentType = typeof args[0];

      if (firstArgumentType === 'number' || firstArgumentType === 'string') {
        let comparator: ReturnType<typeof findById> | ReturnType<typeof findByName>;
        if (firstArgumentType === 'string') {
          comparator = findByName(args[0] as SidePanelName);
        }
        else {
          comparator = findById(args[0] as SidePanelId);
        }

        const idx = this.activeSidePanels.findIndex(comparator);
        if (idx !== -1) {
          const sidePanel = this.activeSidePanels[idx];
          sidePanel.events.onDespawn?.();

          this.activeSidePanels.splice(idx, 1);
        }
      }
      else if (args[0] === null && args[1] === true) {
        for (const sidePanel of this.activeSidePanels) {
          sidePanel.events.onDespawn?.();
        }

        this.activeSidePanels = [];
      }
      else {
        throw new Error('Error parsing function arguments "despawn" sidepanel');
      }
    },
  },
});
